import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import Seo from '../components/Seo';
import { SERVICE_ICONS, IMAGES } from '../mock/mock';

const PageHero = ({ kicker, title, subtitle, image }) => (
  <section className="relative pt-40 pb-20 bg-gradient-to-b from-cream to-ivory">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <div className="animate-fade-up">
        <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold mb-4"><span className="w-8 h-px bg-gold" />{kicker}</span>
        <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-[1.08]">{title}</h1>
        <p className="mt-6 text-lg text-mocha leading-relaxed max-w-xl">{subtitle}</p>
      </div>
      {image && (
        <div className="img-zoom aspect-[4/3] shadow-[0_30px_70px_-30px_rgba(43,36,32,0.35)] animate-fade-up" style={{ animationDelay: '0.12s' }}>
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  </section>
);

export { PageHero };

const Services = () => {
  const { t, lang } = useLang();
  useReveal(lang);
  const items = t('services.items');

  return (
    <div>
      <Seo title="Cosmetic Manufacturing Services — OEM, Private Label & Contract" path="/services"
        description="OEM/ODM, private-label and contract cosmetics manufacturing under GMP (ISO 22716): R&D, formulation, filling, packaging, regulatory notification and turnkey luxury brand development." />
      <PageHero kicker={t('home.servicesKicker')} title={t('services.title')} subtitle={t('services.subtitle')} image={IMAGES.manufacturing} />
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-6">
          {items.map((s, i) => {
            const Icon = Icons[SERVICE_ICONS[i]] || Icons.Sparkles;
            return (
              <div key={i} className="reveal group flex flex-col md:flex-row gap-6 md:gap-10 items-start p-8 md:p-10 bg-cream hover:bg-espresso transition-colors duration-500">
                <div className="flex items-center gap-6 md:w-1/3">
                  <span className="font-serif text-4xl text-gold/60 group-hover:text-gold-light transition-colors">{String(i + 1).padStart(2, '0')}</span>
                  <div className="w-14 h-14 flex items-center justify-center bg-ivory group-hover:bg-espresso/40 text-gold transition-colors"><Icon className="w-6 h-6" /></div>
                  <h3 className="font-serif text-2xl text-espresso group-hover:text-ivory transition-colors">{s.title}</h3>
                </div>
                <p className="md:w-2/3 text-mocha group-hover:text-ivory/80 leading-relaxed transition-colors">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
      <section className="py-20 bg-cream text-center">
        <div className="max-w-2xl mx-auto px-6 reveal">
          <h2 className="font-serif text-3xl md:text-4xl text-espresso">{t('home.ctaTitle')}</h2>
          <Link to="/contact" className="group mt-8 inline-flex items-center gap-3 px-10 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors">
            {t('home.ctaButton')}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
